import * as React from 'react';
import { useState, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import moment from 'moment';
import { useRouter } from 'found';
import { createFragmentContainer, graphql } from 'react-relay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons';
import Card from '../../../sharedComponents/core/Cards/Card';
import { Theme } from 'helpers/theme';
import CardPagination from './CardPagination';
import { ExpiringSoonCard_company } from './__generated__/ExpiringSoonCard_company.graphql';

type Bucket = 'all' | 'fourteen' | 'twentyEight' | 'sixty';

const BUCKET_COLOURS: Record<Exclude<Bucket, 'all'>, string> = {
  fourteen: '#e05c5c',
  twentyEight: '#e09a3a',
  sixty: '#c4b84a'
};

const FILTERS: ReadonlyArray<{ key: Bucket; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'fourteen', label: '0–14 days' },
  { key: 'twentyEight', label: '15–28 days' },
  { key: 'sixty', label: '29–60 days' }
];

const useStyles = createUseStyles((theme: Theme) => ({
  cardRoot: {
    height: '100%'
  },
  card: {
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    height: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  iconContainer: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#F4F5F7',
    borderRadius: 8,
    fontSize: 14
  },
  title: {
    fontWeight: 800,
    fontSize: 14,
    color: theme.colors.primaryBlack
  },
  description: {
    fontSize: 12,
    color: '#6A728F',
    marginTop: 2
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  pill: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    cursor: 'pointer',
    background: '#F4F5F7',
    color: '#6A728F',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: '#EBEDF2'
    }
  },
  pillActive: {
    background: '#0950ee',
    color: theme.colors.primaryWhite,
    '&:hover': {
      background: '#0a4cdf'
    }
  },
  table: {
    display: 'grid',
    gridTemplateColumns: 'minmax(90px, auto) minmax(0, 1.1fr) minmax(0, 1.4fr)',
    rowGap: 2
  },
  headerRow: {
    display: 'contents',
    '& > div': {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      color: '#9aa0b8',
      padding: '6px 10px',
      borderBottom: '1px solid #EEF0F4'
    }
  },
  row: {
    display: 'contents',
    cursor: 'pointer',
    '& > div': {
      padding: '8px 10px',
      transition: 'background 0.15s',
      display: 'flex',
      alignItems: 'center'
    },
    '&:hover > div': {
      background: '#F6F7FB'
    },
    '&:hover > div:first-child': {
      borderTopLeftRadius: 6,
      borderBottomLeftRadius: 6
    },
    '&:hover > div:last-child': {
      borderTopRightRadius: 6,
      borderBottomRightRadius: 6
    }
  },
  daysPill: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    padding: '0 10px',
    borderRadius: 11,
    fontSize: 11,
    fontWeight: 700,
    color: 'white',
    whiteSpace: 'nowrap'
  },
  delegate: {
    fontSize: 13,
    color: theme.colors.primaryBlack,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  itemName: {
    fontSize: 13,
    color: '#3A3F58',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 16px',
    textAlign: 'center'
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#3d4566'
  },
  emptySubtitle: {
    fontSize: 12,
    color: '#9aa0b8'
  }
}));

function bucketFor(daysAway: number): Exclude<Bucket, 'all'> | null {
  if (daysAway < 0) return null;
  if (daysAway <= 14) return 'fourteen';
  if (daysAway <= 28) return 'twentyEight';
  if (daysAway <= 60) return 'sixty';
  return null;
}

function daysLabel(daysAway: number): string {
  if (daysAway < 0) return 'Expired';
  if (daysAway === 0) return 'Today';
  if (daysAway === 1) return '1 day';
  return `${daysAway} days`;
}

type Props = {
  company: ExpiringSoonCard_company;
  className?: string;
};

function ExpiringSoonCard({ company, className }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router } = useRouter();
  const pageSize = 6;
  const [filter, setFilter] = useState<Bucket>('all');
  const [page, setPage] = useState(0);

  const now = useMemo(() => moment().startOf('day'), []);

  const rows = useMemo(() => {
    const edges = company?.upcomingExpirations?.edges ?? [];
    return edges
      .map((edge) => {
        if (!edge) return null;
        const days = moment(edge.expiryDate).startOf('day').diff(now, 'days');
        return {
          delegateUUID: edge.delegateUUID,
          delegateName: edge.delegateName,
          itemName: edge.itemName,
          expiryDate: edge.expiryDate,
          daysAway: days,
          bucket: bucketFor(days)
        };
      })
      .filter(
        (r): r is NonNullable<typeof r> => r !== null && r.bucket !== null
      )
      .sort((a, b) => a.daysAway - b.daysAway);
  }, [company, now]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => r.bucket === filter);
  }, [rows, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const visible = filtered.slice(start, start + pageSize);

  return (
    <Card className={classNames(classes.cardRoot, className)} padding="none">
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.iconContainer}>
            <FontAwesomeIcon icon={faHourglassHalf} color="#6A728F" />
          </div>
          <div>
            <div className={classes.title}>Expiring Soon</div>
            <div className={classes.description}>
              Certificates and checks lapsing in the next 60 days
            </div>
          </div>
        </div>

        <div className={classes.filterRow}>
          {FILTERS.map((f) => (
            <div
              key={f.key}
              className={classNames(classes.pill, {
                [classes.pillActive]: filter === f.key
              })}
              onClick={() => {
                setFilter(f.key);
                setPage(0);
              }}
            >
              {f.label}
            </div>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className={classes.empty}>
            <span className={classes.emptyTitle}>Nothing to see here!</span>
            <span className={classes.emptySubtitle}>
              {filter === 'all'
                ? 'No upcoming expirations.'
                : 'Nothing in this range.'}
            </span>
          </div>
        ) : (
          <>
            <div className={classes.table}>
              <div className={classes.headerRow}>
                <div>Days away</div>
                <div>Delegate</div>
                <div>Course</div>
              </div>
              {visible.map((row, i) => (
                <div
                  key={start + i}
                  className={classes.row}
                  onClick={() =>
                    router.push(`/app/delegates/${row.delegateUUID}`)
                  }
                >
                  <div>
                    <span
                      className={classes.daysPill}
                      style={{
                        backgroundColor: row.bucket
                          ? BUCKET_COLOURS[row.bucket]
                          : '#9aa0b8'
                      }}
                      title={moment(row.expiryDate).format('LLLL')}
                    >
                      {daysLabel(row.daysAway)}
                    </span>
                  </div>
                  <div className={classes.delegate} title={row.delegateName}>
                    {row.delegateName}
                  </div>
                  <div className={classes.itemName} title={row.itemName}>
                    {row.itemName}
                  </div>
                </div>
              ))}
            </div>
            <CardPagination
              page={safePage}
              pageSize={pageSize}
              totalItems={filtered.length}
              onChange={setPage}
            />
          </>
        )}
      </div>
    </Card>
  );
}

export default createFragmentContainer(ExpiringSoonCard, {
  company: graphql`
    fragment ExpiringSoonCard_company on Company {
      upcomingExpirations(page: { limit: 80 }) {
        edges {
          delegateUUID
          delegateName
          itemName
          expiryDate
        }
      }
    }
  `
});
